import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import type { DesignTemplateResponse } from '@mathflat/domain/@entities/DesignTemplate/api'
import type { CoverDataType } from '@mathflat/domain/@entities/DesignTemplate/constant'
import type { CoverDataItem } from '@mathflat/domain/@entities/DesignTemplate/CoverData/dto'
import { DesignTemplate } from '@mathflat/domain/@entities/DesignTemplate/dto.ts'
import { computed, isObservable, makeObservable, observable } from 'mobx'

import type { Tuple } from '@/types'

export class WorkbookDesignTemplate<
  T extends Record<
    keyof T,
    CoverDataItem<CoverDataType, string> | null
  > = WorkbookEntity.CustomSignatureWorkbookCoverDataT,
> extends DesignTemplate<T> {
  constructor(data: DesignTemplate<T> | DesignTemplateResponse<T>) {
    super(data)

    // 이렇게 까지 하지 않으면 deep하게 observable이 먹지 않음(deep option 다 써봐도 안 됨)
    makeObservable(this.coverData, {
      preAssignedCoverData: observable.deep,
      customCoverData: observable.deep,
      width: observable,
      height: observable,
      allItems: computed,
    })
    ;(Object.values(this.coverData.preAssignedCoverData) as Array<CoverDataItem | null>).forEach(
      (data) => {
        if (data && data.props && !isObservable(data.props)) {
          makeObservable(data, { props: observable.deep })
        }
      },
    )

    this.coverData.customCoverData.forEach((data: CoverDataItem) => {
      if (data && data.props && !isObservable(data.props)) {
        makeObservable(data, { props: observable.deep })
      }
    })
  }

  static font = {
    fontFamilies: [
      ['리디바탕', 'RIDIBatang'],
      ['부크크명조', 'BookkMyungjo-Bold'],
      ['제주명조', 'JejuMyeongjo'],
      ['충주김생체', 'ChungjuKimSaeng'],
      ['프리텐다드 light', 'Pretendard-Light'],
      ['프리텐다드 regular', 'Pretendard-Regular'],
      ['프리텐다드 medium', 'Pretendard-Medium'],
      ['프리텐다드 semibold', 'Pretendard-SemiBold'],
      ['프리텐다드 bold', 'Pretendard-Bold'],
      ['오아고딕 extrabold', 'OAGothic-ExtraBold'],
      ['프리젠테이션 medium', 'Freesentation-Medium'],
      ['프리젠테이션 semibold', 'Freesentation-SemiBold'],
      ['프리젠테이션 bold', 'Freesentation-Bold'],
      ['플라이트 산스 regular', 'FlightSans-Regular'],
      ['플라이트 산스 bold', 'FlightSans-Bold'],
      ['HS산토끼체 2.0 Regular', 'HSSantoki-Regular'],
      ['SB 어그로 Light', 'Aggravo-Light'],
      ['MICE 명조 Regular', 'MiceMyungjo-Regular'],
      ['머니그라피 Rounded', 'Moneygraphy-Rounded'],
    ] as const,
    fontSizes: [8, 12, 14, 16, 24, 32, 40, 48, 56],
  }

  static templateColors: Tuple<WorkbookDesignTemplate['color'], string>[] = [
    ['WHITE', '#fff'],
    ['SKY', '#37bef0'],
    ['BLUE_GREEN', '#00ada9'],
    ['GREEN', '#43b149'],
    ['PINK', '#e098c1'],
    ['ORANGE', '#ee782c'],
    ['YELLOW', '#fcc80e'],
  ]

  static templateColorMap = new Map(WorkbookDesignTemplate.templateColors)
}
